import { CloudUpload } from "@mui/icons-material";
import {
	Avatar,
	Box,
	Button,
	Card,
	CardMedia,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import React, { useState } from "react";
import { api_ImageUpload, api_postNewEliteEntry } from "./api"; // Import APIs

const darkTheme = createTheme({
	palette: {
		mode: "dark",
		primary: {
			main: "#90caf9",
		},
		background: {
			default: "#121212",
		},
	},
	typography: {
		fontFamily: "Roboto, sans-serif",
	},
});

const BackgroundWrapper = styled("div")({
	backgroundImage: `url(/bg.jpg)`,
	backgroundSize: "cover",
	backgroundPosition: "center",
	minHeight: "100vh",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
});

const steps = [
	"Personal Details",
	"Business Details",
	"Documents",
	"Review & Submit",
];

const MultiStepForm = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [formData, setFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		mobile: "",
		bussinessType: "",
		bussinessName: "",
		address1: "",
		address2: "",
		zip: "",
		city: "",
		province: "",
		country: "",
		aadharNumber: "",
		instagram_link: "", // Added initialization
		certificate: "",
		registrationDoc: "",
		idProof: "",
	});

	const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

	const isValidMobile = (mobile) => /^\d{10}$/.test(mobile);

	const isValidZip = (zip) => /^\d{5,6}$/.test(zip);

	const isValidAadhar = (aadhar) => /^\d{12}$/.test(aadhar);

	const isValidFile = (file) => {
		const maxSize = 2 * 1024 * 1024; // 2MB
		const validTypes = ["image/jpeg", "image/png", "application/pdf"];
		return file && validTypes.includes(file.type) && file.size <= maxSize;
	};

	const isStepValid = () => {
		switch (activeStep) {
			case 0:
				return (
					formData.first_name &&
					formData.last_name &&
					isValidEmail(formData.email) &&
					isValidMobile(formData.mobile) &&
					formData.instagram_link
				);
			case 1:
				return (
					formData.bussinessType &&
					formData.bussinessName &&
					formData.address1 &&
					formData.address2 &&
					isValidZip(formData.zip) &&
					formData.city &&
					formData.province &&
					formData.country
				);
			case 2:
				return (
					isValidAadhar(formData.aadharNumber) &&
					isValidFile(formData.certificate) &&
					isValidFile(formData.registrationDoc) &&
					isValidFile(formData.idProof)
				);
			default:
				return true;
		}
	};

	const handleNext = async () => {
		if (isStepValid()) {
			if (activeStep === 2) {
				// Upload images
				const uploadedDocs = await Promise.all([
					api_ImageUpload({
						mime: formData.certificate.type,
						name: "pan", // Backend expects "pan" for this document
						image: await convertToBase64(formData.certificate),
					}),
					api_ImageUpload({
						mime: formData.registrationDoc.type,
						name: "act", // Backend expects "act" for this document
						image: await convertToBase64(formData.registrationDoc),
					}),
					api_ImageUpload({
						mime: formData.idProof.type,
						name: "regDoc", // Backend expects "regDoc" for this document
						image: await convertToBase64(formData.idProof),
					}),
				]);

				// Update formData with uploaded URLs
				setFormData((prev) => ({
					...prev,
					certificate: uploadedDocs[0]?.data?.imageURL || null,
					registrationDoc: uploadedDocs[1]?.data?.imageURL || null,
					idProof: uploadedDocs[2]?.data?.imageURL || null,
				}));
			}
			setActiveStep((prev) => prev + 1);
		} else {
			alert(
				"Please fill in all required fields with valid data before proceeding."
			);
		}
	};

	const handleBack = () => {
		setActiveStep((prev) => prev - 1);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleFileChange = (e) => {
		const { name, files } = e.target;
		setFormData((prev) => ({ ...prev, [name]: files[0] }));
	};

	const convertToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result.split(",")[1]); // Remove base64 header
			reader.onerror = (error) => reject(error);
		});
	};

	const handleSubmit = async () => {
		try {
			// Construct the bussinessDocuments object
			const bussinessDocuments = {
				pan: formData.certificate,
				act: formData.registrationDoc,
				regDoc: formData.idProof,
			};

			// Prepare the final payload
			const payload = {
				customer_id: "", // Empty as per requirements
				firstname: formData.first_name,
				lastname: formData.last_name,
				mobile: formData.mobile,
				email: formData.email,
				instagram_link: formData.instagram_link || "",
				aadhar_number: formData.aadharNumber,
				bussinessType: formData.bussinessType,
				bussinessName: formData.bussinessName,
				bussinessAddress: {
					address1: formData.address1,
					address2: formData.address2,
					zip: formData.zip,
					city: formData.city,
					province: formData.province,
					country: formData.country,
				},
				bussinessDocuments,
			};

			const response = await api_postNewEliteEntry(payload);

			if (response?.status === 200) {
				alert("Form submitted successfully!");
				window.location.href = "https://www.sugarcosmetics.com/pages/sugar-lite-landing-page";
			} else {
				alert("Failed to submit form. Please try again.");
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			alert("An error occurred. Please try again.");
		}
	};

	const renderStepContent = (step) => {
		switch (step) {
			case 0:
				return (
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="First Name"
								name="first_name"
								fullWidth
								value={formData.first_name}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Last Name"
								name="last_name"
								fullWidth
								value={formData.last_name}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Email"
								name="email"
								fullWidth
								value={formData.email}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Mobile Number"
								name="mobile"
								fullWidth
								value={formData.mobile}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Instagram Link"
								name="instagram_link"
								fullWidth
								value={formData.instagram_link}
								onChange={handleChange}
								required
							/>
						</Grid>
					</Grid>
				);
			case 1:
				return (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControl fullWidth required>
								<InputLabel>Business Type</InputLabel>
								<Select
									name="bussinessType"
									value={formData.bussinessType}
									onChange={handleChange}
								>
									<MenuItem value="salon">Salon</MenuItem>
									<MenuItem value="academy">Academy</MenuItem>
									<MenuItem value="makeup artist">
										Makeup Artist
									</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Business Name"
								name="bussinessName"
								fullWidth
								value={formData.bussinessName}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Address Line 1"
								name="address1"
								fullWidth
								value={formData.address1}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Address Line 2"
								name="address2"
								fullWidth
								value={formData.address2}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="ZIP Code"
								name="zip"
								fullWidth
								value={formData.zip}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="City"
								name="city"
								fullWidth
								value={formData.city}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Province"
								name="province"
								fullWidth
								value={formData.province}
								onChange={handleChange}
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Country"
								name="country"
								fullWidth
								value={formData.country}
								onChange={handleChange}
								required
							/>
						</Grid>
					</Grid>
				);
			case 2:
				return (
					<Grid container spacing={2} sx={{ padding: "20px" }}>
						{/* Aadhar Number Input */}
						<Grid item xs={12}>
							<TextField
								label="Aadhar Number"
								name="aadharNumber"
								fullWidth
								value={formData.aadharNumber}
								onChange={handleChange}
								required
								variant="outlined"
							/>
						</Grid>

						{/* Document Upload Section */}
						<Grid item xs={12}>
							<Typography
								variant="h5"
								gutterBottom
								sx={{ marginTop: "20px" }}
							>
								Upload Documents
							</Typography>
						</Grid>

						{/* Certificate Upload */}
						<Grid item xs={12} sm={12}>
							<Card
								variant="outlined"
								sx={{
									textAlign: "center",
									padding: 2,
									transition: "0.3s",
									"&:hover": { boxShadow: 4 },
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight="bold"
								>
									Certificate
								</Typography>
								<Box
									mt={2}
									display={"flex"}
									justifyContent={"center"}
								>
									{formData.certificate ? (
										<CardMedia
											component="img"
											height="150"
											image={URL.createObjectURL(
												formData.certificate
											)}
											alt="Certificate Preview"
											sx={{
												borderRadius: 2,
												border: "1px solid #ddd",
												objectFit: "contain",
											}}
										/>
									) : (
										<Avatar
											variant="square"
											sx={{
												width: 150,
												height: 150,
												bgcolor: "#0000001",
											}}
										>
											<CloudUpload fontSize="large" />
										</Avatar>
									)}
								</Box>
								<Tooltip title="Upload your certificate">
									<Button
										variant="contained"
										component="label"
										startIcon={<CloudUpload />}
										sx={{ mt: 2 }}
									>
										Upload
										<input
											type="file"
											name="certificate"
											accept="image/*"
											hidden
											onChange={handleFileChange}
										/>
									</Button>
								</Tooltip>
							</Card>
						</Grid>

						{/* Registration Document Upload */}
						<Grid item xs={12} sm={12}>
							<Card
								variant="outlined"
								sx={{
									textAlign: "center",
									padding: 2,
									transition: "0.3s",
									"&:hover": { boxShadow: 4 },
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight="bold"
								>
									Registration Document
								</Typography>
								<Box
									mt={2}
									display={"flex"}
									justifyContent={"center"}
								>
									{formData.registrationDoc ? (
										<CardMedia
											component="img"
											height="150"
											image={URL.createObjectURL(
												formData.registrationDoc
											)}
											alt="Registration Document Preview"
											sx={{
												borderRadius: 2,
												border: "1px solid #ddd",
												objectFit: "contain",
											}}
										/>
									) : (
										<Avatar
											variant="square"
											sx={{
												width: 150,
												height: 150,
												bgcolor: "#0000001",
											}}
										>
											<CloudUpload fontSize="large" />
										</Avatar>
									)}
								</Box>
								<Tooltip title="Upload your registration document">
									<Button
										variant="contained"
										component="label"
										startIcon={<CloudUpload />}
										sx={{ mt: 2 }}
									>
										Upload
										<input
											type="file"
											name="registrationDoc"
											accept="image/*"
											hidden
											onChange={handleFileChange}
										/>
									</Button>
								</Tooltip>
							</Card>
						</Grid>

						{/* ID Proof Upload */}
						<Grid item xs={12} sm={12}>
							<Card
								variant="outlined"
								sx={{
									textAlign: "center",
									padding: 2,
									transition: "0.3s",
									"&:hover": { boxShadow: 4 },
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight="bold"
								>
									ID Proof
								</Typography>
								<Box
									mt={2}
									display={"flex"}
									justifyContent={"center"}
								>
									{formData.idProof ? (
										<CardMedia
											component="img"
											height="150"
											image={URL.createObjectURL(
												formData.idProof
											)}
											alt="ID Proof Preview"
											sx={{
												borderRadius: 2,
												border: "1px solid #ddd",
												objectFit: "contain",
											}}
										/>
									) : (
										<Avatar
											variant="square"
											sx={{
												width: 150,
												height: 150,
												bgcolor: "#0000001",
											}}
										>
											<CloudUpload fontSize="large" />
										</Avatar>
									)}
								</Box>
								<Tooltip title="Upload your ID proof">
									<Button
										variant="contained"
										component="label"
										startIcon={<CloudUpload />}
										sx={{ mt: 2 }}
									>
										Upload
										<input
											type="file"
											name="idProof"
											accept="image/*"
											hidden
											onChange={handleFileChange}
										/>
									</Button>
								</Tooltip>
							</Card>
						</Grid>
					</Grid>
				);
			case 3:
				return (
					<Box>
						<Typography variant="h4" gutterBottom>
							Review Your Details
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography>
									<strong>First Name:</strong>{" "}
									{formData.first_name}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Last Name:</strong>{" "}
									{formData.last_name}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Email:</strong> {formData.email}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Mobile Number:</strong>{" "}
									{formData.mobile}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Instagram Link:</strong>{" "}
									{formData.instagram_link || "Not provided"}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Business Type:</strong>{" "}
									{formData.bussinessType}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Business Name:</strong>{" "}
									{formData.bussinessName}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Business Address:</strong>
								</Typography>
								<Typography>
									Address Line 1: {formData.address1}
								</Typography>
								<Typography>
									Address Line 2: {formData.address2}
								</Typography>
								<Typography>ZIP: {formData.zip}</Typography>
								<Typography>City: {formData.city}</Typography>
								<Typography>
									Province: {formData.province}
								</Typography>
								<Typography>
									Country: {formData.country}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<Typography>
									<strong>Aadhar Number:</strong>{" "}
									{formData.aadharNumber}
								</Typography>
							</Grid>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Typography variant="h6" gutterBottom>
										<strong>Uploaded Documents:</strong>
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Box>
										<Typography variant="subtitle1">
											Certificate:{" "}
											{formData.certificate ? (
												<Card
													sx={{
														display: "inline-block",
														width: 150,
														height: 150,
														marginLeft: 1,
													}}
												>
													<CardMedia
														component="img"
														image={
															formData.certificate
														}
														alt="Certificate Preview"
														sx={{
															height: "100%",
															objectFit: "cover",
														}}
													/>
												</Card>
											) : (
												<Typography
													variant="body2"
													component="span"
													color="text.secondary"
												>
													Not uploaded
												</Typography>
											)}
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={12}>
									<Box>
										<Typography variant="subtitle1">
											Registration Document:{" "}
											{formData.registrationDoc ? (
												<Card
													sx={{
														display: "inline-block",
														width: 150,
														height: 150,
														marginLeft: 1,
													}}
												>
													<CardMedia
														component="img"
														image={
															formData.registrationDoc
														}
														alt="Registration Document Preview"
														sx={{
															height: "100%",
															objectFit: "cover",
														}}
													/>
												</Card>
											) : (
												<Typography
													variant="body2"
													component="span"
													color="text.secondary"
												>
													Not uploaded
												</Typography>
											)}
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={12}>
									<Box>
										<Typography variant="subtitle1">
											ID Proof:{" "}
											{formData.idProof ? (
												<Card
													sx={{
														display: "inline-block",
														width: 150,
														height: 150,
														marginLeft: 1,
													}}
												>
													<CardMedia
														component="img"
														image={formData.idProof}
														alt="ID Proof Preview"
														sx={{
															height: "100%",
															objectFit: "cover",
														}}
													/>
												</Card>
											) : (
												<Typography
													variant="body2"
													component="span"
													color="text.secondary"
												>
													Not uploaded
												</Typography>
											)}
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</Grid>
						<Box
							mt={4}
							display="flex"
							justifyContent="space-between"
						>
							<Button variant="outlined" onClick={handleBack}>
								Edit
							</Button>
							<Button variant="contained" onClick={handleSubmit}>
								Submit
							</Button>
						</Box>
					</Box>
				);
			default:
				return <Typography>Unknown Step</Typography>;
		}
	};

	return (
		<ThemeProvider theme={darkTheme}>
			<BackgroundWrapper>
				<Container maxWidth="sm">
					<Card sx={{ padding: 4, borderRadius: 2 }}>
						<Stepper activeStep={activeStep} alternativeLabel>
							{steps.map((label) => (
								<Step key={label}>
									<StepLabel>{label}</StepLabel>
								</Step>
							))}
						</Stepper>
						<Box mt={4}>{renderStepContent(activeStep)}</Box>
						{activeStep < steps.length - 1 && (
							<Box
								mt={4}
								display="flex"
								justifyContent="space-between"
							>
								{activeStep > 0 && (
									<Button onClick={handleBack}>Back</Button>
								)}
								<Button
									variant="contained"
									onClick={handleNext}
								>
									Next
								</Button>
							</Box>
						)}
					</Card>
				</Container>
			</BackgroundWrapper>
		</ThemeProvider>
	);
};

export default MultiStepForm;
